import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/lib/auth-store';
import { getJson } from '@/lib/http';
import { canPublishPhotoChecks, canEditPhotoChecks, canAccessPhotoChecksAdvanced, canMakePhotoChecksLive, type PhotoChecksMeta } from '@/lib/photoChecksAccess';
import { apiRequest } from '@/lib/queryClient';

type Snapshot = {
  id: number;
  name: string;
  description: string | null;
  status: 'draft' | 'frozen';
  submissionType: string;
  includedCount: number;
  excludedCount: number;
  itemCount: number;
  manifestHash: string | null;
  createdAt: string | null;
  frozenAt: string | null;
};

type PromptConfig = {
  id: number;
  name: string;
  version: number;
  status: string;
  submissionType: string;
  isActive: boolean;
  model?: string;
};

type MetricDelta = {
  baseline: number | null;
  candidate: number | null;
  absoluteDelta: number | null;
  relativeDelta: number | null;
  status: string;
};

type ComparisonSummary = {
  primarySafetyMetric: string;
  primarySafetyDefinition?: string;
  overallSafetyStatus?: string;
  baseline: Record<string, any>;
  candidate: Record<string, any>;
  deltas: Record<string, MetricDelta>;
  outcomeChangedCount: number;
  regressionItemPostingEventIds: number[];
  improvementItemPostingEventIds: number[];
};

type Comparison = {
  id: number;
  snapshotId: number;
  status: string;
  primarySafetyMetric: string;
  metricsSummary: ComparisonSummary | Record<string, unknown>;
  baselinePromptConfigId: number | null;
  candidatePromptConfigId: number | null;
  errorMessage: string | null;
  createdAt: string | null;
  completedAt: string | null;
};

function formatPct(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function formatMoney(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `$${value.toFixed(3)}`;
}

function deltaTone(status: string | undefined): 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'outline' {
  if (status === 'improved') return 'success';
  if (status === 'regressed') return 'destructive';
  if (status === 'unchanged') return 'secondary';
  return 'outline';
}

function statusLabel(status: string | undefined): string {
  if (status === 'improved') return 'Better';
  if (status === 'regressed') return 'Worse';
  if (status === 'unchanged') return 'Same';
  return status || '—';
}

function ChecklistItem({ ok, children }: { ok: boolean; children: ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <Badge variant={ok ? 'success' : 'destructive'} className="mt-0.5 shrink-0">
        {ok ? 'Done' : 'To do'}
      </Badge>
      <span>{children}</span>
    </li>
  );
}

export default function AIEvalHarnessPage() {
  const [, setLocation] = useLocation();
  const { state } = useAuth();
  const canPublish = canPublishPhotoChecks(state.user);
  const canPrepare = canEditPhotoChecks(state.user) || canAccessPhotoChecksAdvanced(state.user);
  const queryClient = useQueryClient();
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string>('');
  const [candidatePromptId, setCandidatePromptId] = useState<string>('');
  const [baselinePromptId, setBaselinePromptId] = useState<string>('');
  const [disagreementFilter, setDisagreementFilter] = useState<'disagreements' | 'regressions' | 'all'>('regressions');
  const [activeComparisonId, setActiveComparisonId] = useState<number | null>(null);
  const [snapshotName, setSnapshotName] = useState('Reviewed photo test');
  const [maxIncluded, setMaxIncluded] = useState('10');
  const [makeLiveMessage, setMakeLiveMessage] = useState<string | null>(null);

  useEffect(() => {
    const stored = sessionStorage.getItem('photoChecksOpenTrialId');
    if (stored && /^\d+$/.test(stored)) {
      setActiveComparisonId(Number(stored));
      sessionStorage.removeItem('photoChecksOpenTrialId');
    }
  }, []);

  const metaQuery = useQuery({
    queryKey: ['/api/admin/ai-eval/meta'],
    queryFn: () => getJson<PhotoChecksMeta>('/api/admin/ai-eval/meta'),
    enabled: canPrepare,
  });
  const canMakeLive = canMakePhotoChecksLive(state.user, metaQuery.data?.livePublishEnabled);

  const snapshotsQuery = useQuery({
    queryKey: ['/api/admin/ai-eval/snapshots'],
    queryFn: () => getJson<{ ok: true; snapshots: Snapshot[] }>('/api/admin/ai-eval/snapshots'),
    refetchInterval: canPrepare ? 10_000 : false,
    enabled: canPrepare,
  });

  const comparisonsQuery = useQuery({
    queryKey: ['/api/admin/ai-eval/comparisons'],
    queryFn: () => getJson<{ ok: true; comparisons: Comparison[] }>('/api/admin/ai-eval/comparisons'),
    refetchInterval: canPrepare ? 5_000 : false,
    enabled: canPrepare,
  });

  const promptsQuery = useQuery({
    queryKey: ['/api/admin/ai-triage/prompts', 'POSTING'],
    queryFn: () => getJson<{ ok: true; prompts: PromptConfig[] }>('/api/admin/ai-triage/prompts?submissionType=POSTING'),
    enabled: canPrepare,
  });

  const comparisonDetailQuery = useQuery({
    queryKey: ['/api/admin/ai-eval/comparisons', activeComparisonId],
    enabled: canPrepare && activeComparisonId != null,
    queryFn: () => getJson<any>(`/api/admin/ai-eval/comparisons/${activeComparisonId}`),
    refetchInterval: (query) => {
      const status = query.state.data?.comparison?.status;
      return status === 'pending' || status === 'running' ? 3_000 : false;
    },
  });

  const disagreementsQuery = useQuery({
    queryKey: ['/api/admin/ai-eval/comparisons', activeComparisonId, 'disagreements', disagreementFilter],
    enabled: canPrepare && activeComparisonId != null,
    queryFn: () => getJson<any>(`/api/admin/ai-eval/comparisons/${activeComparisonId}/disagreements?filter=${disagreementFilter}`),
    refetchInterval: () => {
      const status = comparisonDetailQuery.data?.comparison?.status;
      return status === 'pending' || status === 'running' ? 3_000 : false;
    },
  });

  const createSnapshotMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('/api/admin/ai-eval/snapshots', 'POST', {
        name: snapshotName,
        submissionType: 'POSTING',
        maxIncluded: Number(maxIncluded) || 10,
        filters: { evidenceRole: 'POSTING_STANDARD' },
      });
      return res.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['/api/admin/ai-eval/snapshots'] });
    },
  });

  const freezeMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest(`/api/admin/ai-eval/snapshots/${id}/freeze`, 'POST', {});
      return res.json();
    },
    onSuccess: async (data) => {
      const id = data?.snapshot?.id;
      if (id) setSelectedSnapshotId(String(id));
      await queryClient.invalidateQueries({ queryKey: ['/api/admin/ai-eval/snapshots'] });
    },
  });

  const startComparisonMutation = useMutation({
    mutationFn: async () => {
      const prompts = promptsQuery.data?.prompts ?? [];
      const live = prompts.find((p) => p.isActive && p.status === 'ACTIVE')
        ?? prompts.find((p) => p.isActive)
        ?? null;
      const baselineId = Number(baselinePromptId)
        || live?.id
        || 0;
      if (!baselineId) {
        throw new Error('Pick a reference standard to compare against.');
      }
      const res = await apiRequest('/api/admin/ai-eval/comparisons', 'POST', {
        snapshotId: Number(selectedSnapshotId),
        candidatePromptConfigId: Number(candidatePromptId),
        baselinePromptConfigId: baselineId,
      });
      return res.json();
    },
    onSuccess: async (data) => {
      const id = data?.comparison?.id;
      if (id) setActiveComparisonId(id);
      await queryClient.invalidateQueries({ queryKey: ['/api/admin/ai-eval/comparisons'] });
    },
  });

  const makeLiveMutation = useMutation({
    mutationFn: async (promptId: number) => {
      const res = await apiRequest(`/api/admin/ai-triage/prompts/${promptId}/publish`, 'POST');
      return res.json();
    },
    onSuccess: async () => {
      setMakeLiveMessage('Proposed change is now live. Human review decisions were not changed.');
      await queryClient.invalidateQueries({ queryKey: ['/api/admin/ai-triage/prompts'] });
    },
  });

  const snapshots = snapshotsQuery.data?.snapshots ?? [];
  const comparisons = comparisonsQuery.data?.comparisons ?? [];
  const allPrompts = promptsQuery.data?.prompts ?? [];
  const activeBaseline = allPrompts.find((p) => p.isActive && p.status === 'ACTIVE')
    ?? allPrompts.find((p) => p.isActive)
    ?? null;
  const draftPrompts = allPrompts.filter((p) => p.status === 'DRAFT');
  const referencePrompts = allPrompts.filter((p) => String(p.id) !== candidatePromptId);
  const frozenSnapshots = snapshots.filter((s) => s.status === 'frozen');
  const draftSnapshots = snapshots.filter((s) => s.status === 'draft');
  const summary = comparisonDetailQuery.data?.comparison?.metricsSummary as ComparisonSummary | undefined;

  const hasActiveBaseline = Boolean(activeBaseline);
  const resolvedBaselineId = baselinePromptId
    || (activeBaseline ? String(activeBaseline.id) : '');
  const hasBaseline = Boolean(resolvedBaselineId);
  const hasDraftCandidate = draftPrompts.length > 0;
  const hasFrozenSnapshot = frozenSnapshots.length > 0;
  const canStart = Boolean(selectedSnapshotId && candidatePromptId && hasBaseline);

  const selectedDisagreements = useMemo(
    () => disagreementsQuery.data?.items ?? [],
    [disagreementsQuery.data],
  );

  if (!canPrepare) {
    return (
      <div className="space-y-4 p-6 max-w-3xl">
        <h1 className="text-2xl font-semibold">Prepare a trial</h1>
        <Alert variant="destructive">
          <AlertTitle>Specialist access required</AlertTitle>
          <AlertDescription>
            Trial preparation is an Advanced workflow. Return to the safety decision page for everyday work.
          </AlertDescription>
        </Alert>
        <Button onClick={() => setLocation('/admin/ai/results')}>Back to decision</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Prepare a trial
          </h1>
          <p className="text-sm text-muted-foreground">
            Specialist workflow: lock reviewed photos and compare live vs proposed.
            For the business decision, go back to <strong>Is it safe to make this live?</strong>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setLocation('/admin/ai/results')}>
            Back to decision
          </Button>
          <Button variant="outline" onClick={() => setLocation('/admin/ai/checks')}>
            Photo standards
          </Button>
        </div>
      </div>

      {!hasActiveBaseline && (
        <Alert>
          <AlertTitle>No live standard yet</AlertTitle>
          <AlertDescription>
            That is fine in sandbox mode. Pick any saved standard as the trial reference below — nothing goes live until you flip the switch.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>How to read results</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            The main safety question: did the proposed change miss rejected photos more often than the live check?
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Rejected photos missed</strong> — AI cleared a photo a reviewer rejected (lower is better).</li>
            <li><strong>Acceptable photos flagged</strong> — AI flagged a photo a reviewer approved.</li>
            <li>Agreement going up does not override worse rejected-photo misses.</li>
          </ul>
        </CardContent>
      </Card>

      <Alert variant={hasBaseline && hasDraftCandidate && hasFrozenSnapshot ? 'success' : 'warning'}>
        <AlertTitle>Trial checklist</AlertTitle>
        <AlertDescription>
          <ul className="mt-3 space-y-3">
            <ChecklistItem ok={hasBaseline}>
              {hasActiveBaseline ? (
                <>
                  Current live check ready:{' '}
                  <strong>#{activeBaseline!.id} {activeBaseline!.name}</strong>
                </>
              ) : hasBaseline ? (
                <>Sandbox reference selected — trials compare drafts without going live</>
              ) : (
                <>Pick a reference standard in step C (any saved draft is fine in sandbox)</>
              )}
            </ChecklistItem>
            <ChecklistItem ok={hasDraftCandidate}>
              {hasDraftCandidate ? (
                <>{draftPrompts.length} proposed change{draftPrompts.length === 1 ? '' : 's'} available</>
              ) : (
                <>
                  No proposed change yet. Create or clone a draft under Photo standards / Prompt Studio.
                </>
              )}
            </ChecklistItem>
            <ChecklistItem ok={hasFrozenSnapshot}>
              {hasFrozenSnapshot
                ? `${frozenSnapshots.length} locked test set${frozenSnapshots.length === 1 ? '' : 's'} ready`
                : 'Create and lock a test set using A → B below'}
            </ChecklistItem>
          </ul>
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm">A</span>
              Create a locked test set
            </CardTitle>
            <CardDescription>
              Pulls reviewed test photos your team already approved or rejected. Start with about 10.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="snapshot-name">Name for this test</Label>
              <Input
                id="snapshot-name"
                value={snapshotName}
                onChange={(e) => setSnapshotName(e.target.value)}
                placeholder="e.g. March posting check"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="max-included">How many photos?</Label>
              <Input
                id="max-included"
                value={maxIncluded}
                onChange={(e) => setMaxIncluded(e.target.value)}
                placeholder="10"
              />
            </div>
            <Button
              onClick={() => createSnapshotMutation.mutate()}
              disabled={createSnapshotMutation.isPending || !snapshotName.trim()}
            >
                {createSnapshotMutation.isPending ? 'Creating…' : 'Create locked test set'}
              </Button>
            {createSnapshotMutation.isError && (
              <p className="text-sm text-destructive">{(createSnapshotMutation.error as Error).message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              These come from reviewed test photos that already have usable evidence and a human decision.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm">B</span>
              Lock the test set
            </CardTitle>
            <CardDescription>
              Locks those photos and the human approve/reject answers so the test stays fair.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {draftSnapshots.length === 0 && frozenSnapshots.length === 0 && (
              <p className="text-muted-foreground">Nothing to lock yet. Finish Step 1 first.</p>
            )}
            {draftSnapshots.map((snapshot) => (
              <div key={snapshot.id} className="flex items-center justify-between gap-2 rounded border p-2">
                <div>
                  <div className="font-medium text-foreground">#{snapshot.id} {snapshot.name}</div>
                  <div className="text-xs text-muted-foreground">{snapshot.includedCount} photos · not locked yet</div>
                </div>
                <Button size="sm" disabled={freezeMutation.isPending} onClick={() => freezeMutation.mutate(snapshot.id)}>
                  Lock
                </Button>
              </div>
            ))}
            {draftSnapshots.length === 0 && frozenSnapshots.length > 0 && (
              <p className="text-muted-foreground">All current test sets are locked. Go to C.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm">C</span>
              Compare reference vs proposed change
            </CardTitle>
            <CardDescription>
              Runs a reference standard and your draft on the same locked photos.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>{hasActiveBaseline ? 'Current live check' : 'Reference standard (sandbox)'}</Label>
              {hasActiveBaseline ? (
                <div className="rounded border bg-muted/40 p-2 text-xs font-medium">
                  #{activeBaseline!.id} {activeBaseline!.name}
                </div>
              ) : (
                <Select
                  value={baselinePromptId}
                  onValueChange={setBaselinePromptId}
                  disabled={referencePrompts.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={referencePrompts.length === 0 ? 'Create a draft first' : 'Choose reference'} />
                  </SelectTrigger>
                  <SelectContent>
                    {referencePrompts.map((prompt) => (
                      <SelectItem key={prompt.id} value={String(prompt.id)}>
                        #{prompt.id} {prompt.name} v{prompt.version} ({prompt.status}
                        {prompt.model ? ` · ${prompt.model}` : ''})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Locked test set</Label>
              <Select
                value={selectedSnapshotId}
                onValueChange={setSelectedSnapshotId}
                disabled={frozenSnapshots.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={frozenSnapshots.length === 0 ? 'Lock a test set first' : 'Choose test set'} />
                </SelectTrigger>
                <SelectContent>
                  {frozenSnapshots.map((snapshot) => (
                    <SelectItem key={snapshot.id} value={String(snapshot.id)}>
                      #{snapshot.id} {snapshot.name} ({snapshot.includedCount} photos)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Proposed change</Label>
              <Select
                value={candidatePromptId}
                onValueChange={setCandidatePromptId}
                disabled={draftPrompts.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={draftPrompts.length === 0 ? 'Propose a change under Photo standards first' : 'Choose proposed change'} />
                </SelectTrigger>
                <SelectContent>
                  {draftPrompts.map((prompt) => (
                    <SelectItem key={prompt.id} value={String(prompt.id)}>
                      #{prompt.id} {prompt.name} v{prompt.version}
                      {prompt.model ? ` · ${prompt.model}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={() => startComparisonMutation.mutate()}
              disabled={!canStart || startComparisonMutation.isPending}
            >
              {startComparisonMutation.isPending ? 'Starting…' : 'Run change trial'}
            </Button>
            {!hasBaseline && (
              <p className="text-sm text-muted-foreground">
                Pick a reference standard to compare against.
              </p>
            )}
            {startComparisonMutation.isError && (
              <p className="text-sm text-destructive whitespace-pre-wrap">
                {(startComparisonMutation.error as Error).message}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your locked test sets</CardTitle>
          <CardDescription>
            “Not locked” means you can still change it. “Locked” means ready for a change trial.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {snapshots.length === 0 ? (
            <p className="text-sm text-muted-foreground">No test sets yet. Use Step 1.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Photos included</TableHead>
                  <TableHead>Skipped</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {snapshots.map((snapshot) => (
                  <TableRow key={snapshot.id}>
                    <TableCell className="font-mono text-xs">{snapshot.id}</TableCell>
                    <TableCell>{snapshot.name}</TableCell>
                    <TableCell>
                      <Badge variant={snapshot.status === 'frozen' ? 'success' : 'secondary'}>
                        {snapshot.status === 'frozen' ? 'Locked' : 'Not locked'}
                      </Badge>
                    </TableCell>
                    <TableCell>{snapshot.includedCount}</TableCell>
                    <TableCell>{snapshot.excludedCount}</TableCell>
                    <TableCell>
                      {snapshot.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={freezeMutation.isPending}
                          onClick={() => freezeMutation.mutate(snapshot.id)}
                        >
                          Lock
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Change trials you have run</CardTitle>
          <CardDescription>
            Wait until status says completed, then open results. Running a trial never makes a change live.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {comparisons.length === 0 ? (
            <p className="text-sm text-muted-foreground">No change trials yet. Use step C.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Test set</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Live → proposed</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisons.map((comparison) => (
                  <TableRow key={comparison.id}>
                    <TableCell className="font-mono text-xs">{comparison.id}</TableCell>
                    <TableCell>{comparison.snapshotId}</TableCell>
                    <TableCell><Badge>{comparison.status}</Badge></TableCell>
                    <TableCell className="text-xs">
                      #{comparison.baselinePromptConfigId} → #{comparison.candidatePromptConfigId}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => setActiveComparisonId(comparison.id)}>
                        Open results
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {activeComparisonId != null && comparisonDetailQuery.data && (
        <Card>
          <CardHeader>
            <CardTitle>Results — change trial #{activeComparisonId}</CardTitle>
            <CardDescription>
              Status: {comparisonDetailQuery.data.comparison?.status}
              {comparisonDetailQuery.data.comparison?.errorMessage
                ? ` — ${comparisonDetailQuery.data.comparison.errorMessage}`
                : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {summary?.overallSafetyStatus && (
              <Alert variant={summary.overallSafetyStatus === 'regressed' ? 'destructive' : 'default'}>
                <AlertTitle>
                  Overall:{' '}
                  {summary.overallSafetyStatus === 'improved' && 'Proposed change looks safer on rejected photos missed'}
                  {summary.overallSafetyStatus === 'regressed' && 'Proposed change performed worse on rejected photos missed — do not make live'}
                  {summary.overallSafetyStatus === 'unchanged' && 'Rejected photos missed about the same'}
                  {summary.overallSafetyStatus === 'unknown' && 'Not enough clean data to call this an improvement'}
                </AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>
                    We judge safety mainly by rejected photos missed (AI cleared a photo a human rejected).
                    {summary.candidate?.falseClearDisplay
                      ? ` Proposed: ${String(summary.candidate.falseClearDisplay)
                        .replace(/false clears/g, 'rejected photos missed')
                        .replace(/successfully evaluated gold rejects/g, 'rejected photos we could score')}`
                      : ''}
                  </p>
                  {canMakeLive && comparisonDetailQuery.data.comparison?.candidatePromptConfigId && (
                    <Button
                      size="sm"
                      disabled={makeLiveMutation.isPending || summary.overallSafetyStatus === 'regressed'}
                      onClick={() => {
                        const id = Number(comparisonDetailQuery.data.comparison.candidatePromptConfigId);
                        const ok = window.confirm(
                          'Make this proposed change live?\n\n'
                          + 'This replaces the current live photo check for this submission type. '
                          + 'It does not change human review decisions — people remain responsible for compliance.\n\n'
                          + 'Only do this after you have inspected the changed decisions below.',
                        );
                        if (ok) makeLiveMutation.mutate(id);
                      }}
                    >
                      Make live (separate from trial)
                    </Button>
                  )}
                  {canPublish && !canMakeLive && (
                    <p className="text-muted-foreground">
                      Live publish is off — trials only until you set PHOTO_CHECKS_LIVE_PUBLISH_ENABLED.
                    </p>
                  )}
                  {makeLiveMessage && <p className="text-emerald-800">{makeLiveMessage}</p>}
                  {makeLiveMutation.isError && (
                    <p className="text-destructive">{(makeLiveMutation.error as Error).message}</p>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {summary?.deltas && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>What we measured</TableHead>
                    <TableHead>Current live check</TableHead>
                    <TableHead>Proposed change</TableHead>
                    <TableHead>Change</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <div className="font-medium">Rejected photos missed</div>
                      <div className="text-xs text-muted-foreground">AI said OK; reviewer said reject</div>
                    </TableCell>
                    <TableCell className="text-xs whitespace-normal max-w-[280px]">
                      {(summary.baseline?.falseClearDisplay as string | undefined)
                        ?.replace(/false clears/g, 'rejected photos missed')
                        ?.replace(/successfully evaluated gold rejects/g, 'rejected photos we could score')
                        ?? `${summary.baseline?.falseClearCount ?? 0} / ${summary.baseline?.successfullyEvaluatedGoldRejects ?? 0}`}
                    </TableCell>
                    <TableCell className="text-xs whitespace-normal max-w-[280px]">
                      {(summary.candidate?.falseClearDisplay as string | undefined)
                        ?.replace(/false clears/g, 'rejected photos missed')
                        ?.replace(/successfully evaluated gold rejects/g, 'rejected photos we could score')
                        ?? `${summary.candidate?.falseClearCount ?? 0} / ${summary.candidate?.successfullyEvaluatedGoldRejects ?? 0}`}
                    </TableCell>
                    <TableCell>{formatPct(summary.deltas.falseClearRate?.absoluteDelta)}</TableCell>
                    <TableCell><Badge variant={deltaTone(summary.deltas.falseClearRate?.status)}>{statusLabel(summary.deltas.falseClearRate?.status)}</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <div className="font-medium">How many rejects we could score</div>
                      <div className="text-xs text-muted-foreground">Successful runs / all human rejects in the set</div>
                    </TableCell>
                    <TableCell>
                      {summary.baseline?.successfullyEvaluatedGoldRejects ?? 0}/{summary.baseline?.goldRejectItems ?? 0}
                    </TableCell>
                    <TableCell>
                      {summary.candidate?.successfullyEvaluatedGoldRejects ?? 0}/{summary.candidate?.goldRejectItems ?? 0}
                    </TableCell>
                    <TableCell>{formatPct(summary.deltas.goldRejectCoverageRate?.absoluteDelta)}</TableCell>
                    <TableCell><Badge variant={deltaTone(summary.deltas.goldRejectCoverageRate?.status)}>{statusLabel(summary.deltas.goldRejectCoverageRate?.status)}</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <div className="font-medium">Acceptable photos flagged</div>
                      <div className="text-xs text-muted-foreground">AI flagged; reviewer approved</div>
                    </TableCell>
                    <TableCell className="text-xs whitespace-normal max-w-[280px]">
                      {(summary.baseline?.falseFlagDisplay as string | undefined)
                        ?.replace(/false flags/g, 'acceptable photos flagged')
                        ?.replace(/successfully evaluated gold approves/g, 'approved photos we could score')
                        ?? formatPct(summary.deltas.falseFlagRate?.baseline)}
                    </TableCell>
                    <TableCell className="text-xs whitespace-normal max-w-[280px]">
                      {(summary.candidate?.falseFlagDisplay as string | undefined)
                        ?.replace(/false flags/g, 'acceptable photos flagged')
                        ?.replace(/successfully evaluated gold approves/g, 'approved photos we could score')
                        ?? formatPct(summary.deltas.falseFlagRate?.candidate)}
                    </TableCell>
                    <TableCell>{formatPct(summary.deltas.falseFlagRate?.absoluteDelta)}</TableCell>
                    <TableCell><Badge variant={deltaTone(summary.deltas.falseFlagRate?.status)}>{statusLabel(summary.deltas.falseFlagRate?.status)}</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Agreement with reviewers</TableCell>
                    <TableCell>{formatPct(summary.deltas.agreementRate?.baseline)}</TableCell>
                    <TableCell>{formatPct(summary.deltas.agreementRate?.candidate)}</TableCell>
                    <TableCell>{formatPct(summary.deltas.agreementRate?.absoluteDelta)}</TableCell>
                    <TableCell><Badge variant={deltaTone(summary.deltas.agreementRate?.status)}>{statusLabel(summary.deltas.agreementRate?.status)}</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Photos where live and draft disagreed</TableCell>
                    <TableCell colSpan={2}>{summary.outcomeChangedCount}</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell><Badge variant="outline">info</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Photos that failed to run</TableCell>
                    <TableCell>{summary.baseline?.failedItems ?? 0}</TableCell>
                    <TableCell>{summary.candidate?.failedItems ?? 0}</TableCell>
                    <TableCell>{summary.deltas.failedItems?.absoluteDelta ?? '—'}</TableCell>
                    <TableCell><Badge variant={deltaTone(summary.deltas.failedItems?.status)}>{statusLabel(summary.deltas.failedItems?.status)}</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Est. cost</TableCell>
                    <TableCell>{formatMoney(summary.deltas.totalEstimatedCostUsd?.baseline)}</TableCell>
                    <TableCell>{formatMoney(summary.deltas.totalEstimatedCostUsd?.candidate)}</TableCell>
                    <TableCell>{formatMoney(summary.deltas.totalEstimatedCostUsd?.absoluteDelta)}</TableCell>
                    <TableCell><Badge variant={deltaTone(summary.deltas.totalEstimatedCostUsd?.status)}>{statusLabel(summary.deltas.totalEstimatedCostUsd?.status)}</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Average time per photo</TableCell>
                    <TableCell>{summary.deltas.avgLatencyMs?.baseline ?? '—'} ms</TableCell>
                    <TableCell>{summary.deltas.avgLatencyMs?.candidate ?? '—'} ms</TableCell>
                    <TableCell>{summary.deltas.avgLatencyMs?.absoluteDelta ?? '—'} ms</TableCell>
                    <TableCell><Badge variant={deltaTone(summary.deltas.avgLatencyMs?.status)}>{statusLabel(summary.deltas.avgLatencyMs?.status)}</Badge></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}

            <div className="flex items-center gap-3">
              <Select value={disagreementFilter} onValueChange={(v) => setDisagreementFilter(v as any)}>
                <SelectTrigger className="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regressions">Performed worse only</SelectItem>
                  <SelectItem value="disagreements">Changed decisions</SelectItem>
                  <SelectItem value="all">All differences</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">{selectedDisagreements.length} photos</span>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Photo</TableHead>
                  <TableHead>What the reviewer decided</TableHead>
                  <TableHead>Live AI</TableHead>
                  <TableHead>New AI</TableHead>
                  <TableHead>Same photo + brief?</TableHead>
                  <TableHead>Performed worse?</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedDisagreements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-sm text-muted-foreground">
                      No rows in this filter yet.
                    </TableCell>
                  </TableRow>
                ) : selectedDisagreements.map((row: any) => (
                  <TableRow key={row.postingEventId}>
                    <TableCell className="font-mono text-xs">{row.postingEventId}</TableCell>
                    <TableCell className="text-xs whitespace-normal max-w-[220px]">
                      <div className="font-medium">
                        {row.snapshotItem?.goldOutcome === 'REJECT' ? 'Rejected' : row.snapshotItem?.goldOutcome === 'APPROVE' ? 'Approved' : row.snapshotItem?.goldOutcome}
                      </div>
                      {row.snapshotItem?.evidenceSecureUrl ? (
                        <a
                          href={row.snapshotItem.evidenceSecureUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="block mt-1"
                        >
                          <img
                            src={row.snapshotItem.evidenceSecureUrl}
                            alt={`Store photo ${row.snapshotItem.evidenceAssetId}`}
                            className="max-h-24 rounded border object-contain"
                          />
                        </a>
                      ) : (
                        <div className="text-muted-foreground">No image link saved</div>
                      )}
                      <div className="text-muted-foreground truncate mt-1">
                        {(row.snapshotItem?.frozenContext?.postingInstructionsText || '').slice(0, 80) || 'No posting brief saved'}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs whitespace-normal max-w-[220px]">
                      <div>{row.baseline?.modelFlagged ? 'Flagged for review' : 'Cleared'}</div>
                      <div className="text-muted-foreground">
                        {(row.baseline?.modelReasons || []).join(', ') || 'no reasons'}
                      </div>
                      <div className="text-muted-foreground">
                        {String((row.baseline?.modelObservations as any)?.notes || '').slice(0, 120) || '—'}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs whitespace-normal max-w-[220px]">
                      <div>{row.candidate?.modelFlagged ? 'Flagged for review' : 'Cleared'}</div>
                      <div className="text-muted-foreground">
                        {(row.candidate?.modelReasons || []).join(', ') || 'no reasons'}
                      </div>
                      <div className="text-muted-foreground">
                        {String((row.candidate?.modelObservations as any)?.notes || '').slice(0, 120) || '—'}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.inputsMatch ? <Badge variant="success">Yes</Badge> : <Badge variant="destructive">No</Badge>}
                    </TableCell>
                    <TableCell className="text-xs whitespace-normal max-w-[200px]">
                      {row.isRegression ? <Badge variant="destructive">Yes</Badge> : 'No'}
                      {row.regressionReason && (
                        <div className="text-muted-foreground mt-1">{row.regressionReason}</div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
